// src/routes/admin-routes.tsx
import { lazy } from 'react';
import type { RoutesProps } from './routes';
import {
  FiUsers,
} from 'react-icons/fi';
import {
  MdOutlineAdminPanelSettings,
  MdWarning,
  MdCancel,
} from 'react-icons/md';
import {
  RiFilePaperLine,
  RiFileWarningLine,
} from 'react-icons/ri';
import {
  GrConnect,
} from 'react-icons/gr';
import {
  AiOutlineAudit,
} from 'react-icons/ai';
import {
  IoDocumentLock,
} from 'react-icons/io5';

// Lazy load components
const AdminSystem = lazy(() => import('@/pages/management/AdminSystem'));
const AdminPermissions = lazy(() => import('@/pages/management/AdminPermissions'));
const ActivityLogs = lazy(() => import('@/pages/management/ActivityLogs'));
const ApiManagement = lazy(() => import('@/pages/management/ApiManagement'));
const ViolationWarnings = lazy(() => import('@/pages/management/ViolationWarnings'));
const LicenseRevocation = lazy(() => import('@/pages/management/LicenseRevocation'));
const LegalDisputes = lazy(() => import('@/pages/management/LegalDisputes'));
const LegalViolations = lazy(() => import('@/pages/management/LegalViolations'));
const LegalEnforcement = lazy(() => import('@/pages/management/LegalEnforcement'));
const AdminPaymentConfirmation = lazy(() => import('@/pages/marketplace/payments/AdminPaymentConfirmation'))


// Admin routes
export const adminRoutes: RoutesProps[] = [
  // System Administration
  {
    path: "/admin/system",
    element: <AdminSystem />,
    name: "Cấu hình hệ thống",
    roles: ["admin"],
    icon: MdOutlineAdminPanelSettings,
  },
  {
    path: "/admin/permissions",
    element: <AdminPermissions />,
    name: "Quản lý người dùng",
    roles: ["admin"],
    icon: FiUsers,
  },
  {
    path: "/admin/activity-logs",
    element: <ActivityLogs />,
    name: "Nhật ký hoạt động",
    roles: ["admin"],
    icon: AiOutlineAudit,
  },
  {
    path: "/partners/api",
    element: <ApiManagement />,
    name: "Quản lý API",
    roles: ["admin"],
    icon: GrConnect,
  },
  {
    path: '/admin/payment-confirmation',
    name: "Xác nhận thanh toán",
    element: <AdminPaymentConfirmation />,
    roles: ['admin', 'staff','legal']
  }
];

// Monitor routes
export const monitorRoutes: RoutesProps[] = [
  {
    path: "/monitoring/warnings",
    element: <ViolationWarnings />,
    name: "Gửi cảnh báo vi phạm",
    roles: ["monitor"],
    icon: MdWarning,
  },
  {
    path: "/monitoring/revocation",
    element: <LicenseRevocation />,
    name: "Thu hồi giấy phép",
    roles: ["monitor"],
    icon: MdCancel,
  },
];

// Legal routes
export const legalRoutes: RoutesProps[] = [
  {
    path: "/legal/disputes",
    element: <LegalDisputes />,
    name: "Giải quyết tranh chấp",
    roles: ["legal"],
    icon: RiFilePaperLine,
  },
  {
    path: "/legal/violations",
    element: <LegalViolations />,
    name: "Xử lý vi phạm",
    roles: ["legal"],
    icon: RiFileWarningLine,
  },
  {
    path: "/legal/enforcement",
    element: <LegalEnforcement />,
    name: "Thực thi luật bản quyền",
    roles: ["legal"],
    icon: IoDocumentLock,
  },
];

// Combined admin system routes
export const adminSystemRoutes: RoutesProps[] = [
  ...adminRoutes,
  ...monitorRoutes,
  ...legalRoutes,
];

// Navigation structure for admin features
export const adminNavigationStructure = {
  systemAdmin: {
    key: 'system-admin',
    translationKey: 'systemAdministration',
    icon: MdOutlineAdminPanelSettings,
    roles: ['admin'],
    children: [
      {
        key: 'system-config',
        translationKey: 'systemConfig',
        path: '/admin/system',
        icon: MdOutlineAdminPanelSettings,
        roles: ['admin'],
      },
      {
        key: 'user-management',
        translationKey: 'userManagement',
        path: '/admin/permissions',
        icon: FiUsers,
        roles: ['admin'],
      },
      {
        key: 'activity-logs',
        translationKey: 'activityLogs',
        path: '/admin/activity-logs',
        icon: AiOutlineAudit,
        roles: ['admin'],
      },
      {
        key: 'api-management',
        translationKey: 'apiManagement',
        path: '/partners/api',
        icon: GrConnect,
        roles: ['admin'],
      },
    ],
  },
  monitoring: {
    key: 'monitoring',
    translationKey: 'monitoring',
    icon: MdWarning,
    roles: ['monitor'],
    children: [
      {
        key: 'violation-warnings',
        translationKey: 'violationWarnings',
        path: '/monitoring/warnings',
        icon: MdWarning,
        roles: ['monitor'],
      },
      {
        key: 'license-revocation',
        translationKey: 'licenseRevocation',
        path: '/monitoring/revocation',
        icon: MdCancel,
        roles: ['monitor'],
      },
    ],
  },
  legal: {
    key: 'legal',
    translationKey: 'legal',
    icon: RiFilePaperLine,
    roles: ['legal'],
    children: [
      {
        key: 'legal-disputes',
        translationKey: 'legalDisputes',
        path: '/legal/disputes',
        icon: RiFilePaperLine,
        roles: ['legal'],
      },
      {
        key: 'legal-violations',
        translationKey: 'legalViolations',
        path: '/legal/violations',
        icon: RiFileWarningLine,
        roles: ['legal'],
      },
      {
        key: 'legal-enforcement',
        translationKey: 'legalEnforcement',
        path: '/legal/enforcement',
        icon: IoDocumentLock,
        roles: ['legal'],
      },
    ],
  },
};

// Helper functions
export const getAdminRoutesForRole = (userRoles: string[]): RoutesProps[] => {
  return adminSystemRoutes.filter((route) => {
    if (!route.roles || route.roles.length === 0) return true;
    return route.roles.some((role) => userRoles.includes(role));
  });
};

export const hasAccessToAdminFeatures = (userRoles: string[]): boolean => {
  const adminRoles = ['admin', 'staff', 'monitor', 'legal'];
  return userRoles.some(role => adminRoles.includes(role));
};

export const canAccessSystemAdmin = (userRoles: string[]): boolean => {
  return userRoles.includes('admin');
};

export const canAccessMonitoring = (userRoles: string[]): boolean => {
  return userRoles.includes('monitor') || userRoles.includes('admin');
};

export const canAccessLegal = (userRoles: string[]): boolean => {
  return userRoles.includes('legal') || userRoles.includes('admin');
};

export default adminSystemRoutes;
